// Two-row variant of StatChip used for the Home Sleep tile. Primary is
// "last night" (or "tonight so far" when a night sleep is in progress),
// secondary is naps today (or last night, demoted when tonight is primary).

import { fmtDur } from '../../lib/format.ts';

type Props = {
  lastNightMins: number;
  napsTodayMins: number;
  tonightSoFarMins: number | null;
  /** Minutes since the last completed sleep ended. Null when there is no
   *  prior sleep on file or a sleep is currently in progress. Surfaces the
   *  current wake window — parents track it to time the next nap. */
  awakeSinceMins: number | null;
};

export function SleepStatChip({
  lastNightMins,
  napsTodayMins,
  tonightSoFarMins,
  awakeSinceMins,
}: Props) {
  const inNight = tonightSoFarMins != null;
  const primaryVal = inNight ? tonightSoFarMins : lastNightMins;
  const primaryLabel = inNight ? 'tonight' : 'night';
  const secondaryVal = inNight ? lastNightMins : napsTodayMins;
  const secondaryLabel = inNight ? 'prev' : 'naps';
  const showAwake = !inNight && awakeSinceMins != null && awakeSinceMins >= 0;

  return (
    <div
      style={{
        flex: 1.6,
        padding: '12px 14px',
        background: 'var(--bg-card)',
        borderRadius: 16,
        border: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        Sleep
        {inNight && (
          <span
            aria-hidden
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: 'var(--accent)',
              animation: 'lt-pulse 1.4s ease-in-out infinite',
            }}
          />
        )}
        {showAwake && (
          <span
            className="lt-tabular"
            style={{
              marginLeft: 'auto',
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--text-2)',
              textTransform: 'none',
              letterSpacing: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 5,
                height: 5,
                borderRadius: 999,
                background: 'var(--text-3)',
              }}
            />
            {fmtDur(awakeSinceMins!)} awake
          </span>
        )}
      </div>

      <Row value={primaryVal} label={primaryLabel} primary />
      <Row value={secondaryVal} label={secondaryLabel} />
    </div>
  );
}

function Row({ value, label, primary }: { value: number; label: string; primary?: boolean }) {
  const empty = value <= 0;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 6,
        minWidth: 0,
      }}
    >
      <span
        className="lt-serif lt-tabular"
        style={{
          fontSize: primary ? 20 : 13,
          lineHeight: 1.05,
          color: primary ? 'var(--text-1)' : 'var(--text-3)',
          letterSpacing: -0.01,
          whiteSpace: 'nowrap',
        }}
      >
        {empty ? '—' : fmtDur(value)}
      </span>
      <span
        style={{
          fontSize: 10,
          color: 'var(--text-3)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </div>
  );
}
